import DebounceControl from "debounce-control";
import { FC, useState } from "react";
import { useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAsyncFn } from "src/hooks";
import { CustomerResponse, requestCustomer } from "src/services/payment";
import { useInstallationStore } from "src/store";
import { useDiscountStore } from "src/store/discount";
import { FormSchema } from "src/utils/formSchema";
import { FormSteps } from "src/types";
import { AnimatedInput } from "./common/animated-input";
import InputLoading from "./form/InputLoading";
import { FormControl, FormField, FormItem, FormMessage } from "./ui/form";
import { AnimatedSelect } from "./AnimatedSelect/AnimatedSelect";

type ContactDetailsFormProps = {
  form: UseFormReturn<FormSchema, unknown>;
  setCodeValid: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<FormSteps>>;
  codeValid: boolean;
};

const ContactDetailsForm: FC<ContactDetailsFormProps> = ({
  form,
  setCodeValid,
  setCurrentStep,
  codeValid,
}) => {
  const { t } = useTranslation();

  const { installationDetails, setInstallationDetails } =
    useInstallationStore();

  const handleWithOutsideLiftChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: boolean
  ) => {
    e &&
      setInstallationDetails({
        withOutsideLift: value,
      });
  };

  return (
    <div style={{ width: "100%" }}>
      <button onClick={() => setCurrentStep(1)} className="back-button">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-arrow-left-icon lucide-arrow-left"
        >
          <path d="m12 19-7-7 7-7" />
          <path d="M19 12H5" />
        </svg>
        <span className="back-button-text">{t("general:goBack")}</span>
      </button>

      <div className="form-title">{t("contactDetails:title")}</div>
      <div className="row ">
        <FormField
          control={form.control}
          name="salutation"
          render={({ field }) => (
            <FormItem className="col-6">
              <FormControl>
                <AnimatedSelect
                  value={
                    field.value
                      ? { value: field.value, label: field.value }
                      : undefined
                  }
                  options={[
                    { label: "Herr", value: "Herr" },
                    { label: "Frau", value: "Frau" },
                  ]}
                  placeholder={t(
                    "contactDetails:inputs:salutation:placeholder"
                  )}
                  onChange={(e: any) => {
                    field.onChange(e.value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="col-6">
              <FormControl>
                <AnimatedInput
                  placeholder={t("contactDetails:inputs:firstName:placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="col-6">
              <FormControl>
                <AnimatedInput
                  placeholder={t("contactDetails:inputs:lastName:placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="row">
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem className="col-6">
              <FormControl>
                <AnimatedInput
                  placeholder={`${t(
                    "contactDetails:inputs:companyName:placeholder"
                  )} (${t("general:optional")})`}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Code setCodeValid={setCodeValid} />
      </div>

      <div className="row">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="col-6">
              <FormControl>
                <AnimatedInput
                  type="email"
                  placeholder={t("contactDetails:inputs:email:placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="telephoneNumber"
          render={({ field }) => (
            <FormItem className="col-6">
              <FormControl>
                <AnimatedInput
                  type="text"
                  placeholder={t(
                    "contactDetails:inputs:telephoneNumber:placeholder"
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="row">
        <FormField
          control={form.control}
          name="contactStreetAndHouseNumber"
          render={({ field }) => (
            <FormItem className="col-12">
              <FormControl>
                <AnimatedInput
                  placeholder={t(
                    "contactDetails:inputs:contactStreetAndHouseNumber:placeholder"
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="contactCityAndZipCode"
          render={({ field }) => (
            <FormItem className="col-12">
              <FormControl>
                <AnimatedInput
                  placeholder={t(
                    "contactDetails:inputs:contactCityAndZipCode:placeholder"
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {codeValid && (
        <div className="radio-wrapper">
          <label className="input-label">
            {t("installations:checkboxes:withOutsideLift:label")}
          </label>

          <div className="radio-container">
            <label
              className="radio-input-label"
              htmlFor="withOutsideLift_false"
            >
              <input
                type="radio"
                id="withOutsideLift_false"
                name="withOutsideLift"
                value="false"
                className="radio-input"
                onChange={(e) => handleWithOutsideLiftChange(e, false)}
                checked={!installationDetails.withOutsideLift}
              />
              {t("installations:checkboxes:withOutsideLift:false")}
            </label>

            <label className="radio-input-label" htmlFor="withOutsideLift_true">
              <input
                type="radio"
                id="withOutsideLift_true"
                name="withOutsideLift"
                value="true"
                className="radio-input"
                onChange={(e) => handleWithOutsideLiftChange(e, true)}
                checked={installationDetails.withOutsideLift}
              />
              {t("installations:checkboxes:withOutsideLift:true")}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export { ContactDetailsForm };

function Code({
  disabled,
  setCodeValid,
}: {
  disabled?: boolean;
  setCodeValid: Function;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");

  const { setDiscountPercentage } = useDiscountStore();
  const { setValue } = useFormContext<FormSchema>();

  const requestCustomerFn = useAsyncFn<{ data: CustomerResponse }>(
    requestCustomer
  );

  const onCodeChange = async (value: string) => {
    setCode(value);
    if (!value.trim()) {
      return;
    }
    const data = await requestCustomerFn.execute({ code: value });
    setDiscountPercentage(Number(data.data.discount));
    setCodeValid(true);

    setValue("salutation", data.data.salutation, { shouldValidate: true });
    setValue("firstName", data.data.firstName, { shouldValidate: true });
    setValue("lastName", data.data.lastName, { shouldValidate: true });
    setValue("companyName", data.data.companyName, { shouldValidate: true });
    setValue("email", data.data.email, { shouldValidate: true });
    setValue("telephoneNumber", data.data.telephoneNumber, {
      shouldValidate: true,
    });
    setValue("contactStreetAndHouseNumber", data.data.streetAndHouseNumber, {
      shouldValidate: true,
    });
    setValue("contactCityAndZipCode", data.data.cityAndZipCode, {
      shouldValidate: true,
    });
  };

  return (
    <div className="col-6 code-container">
      <DebounceControl
        value={code}
        delay={500}
        onDebouncedChange={onCodeChange}
        render={({ value, onChange }) => (
          <AnimatedInput
            disabled={disabled}
            placeholder={`${t("contactDetails:inputs:code:placeholder")} (${t(
              "general:optional"
            )})`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      />

      {requestCustomerFn.loading && <InputLoading />}
    </div>
  );
}
